import type {
  CoreExactMutation,
  CoreExactMutationResponseMap,
  CoreRead,
  CoreReadResponseMap,
} from '@exylon/generated-contract-kernel/types';

import type { ConditionalRead, PanelGeneratedHttpPort, PanelReadOptions } from '../foundation/runtime-port';
import type { IdentityHttpErrorKind } from './identity-http';
import {
  canonicalRefusal, settleRefusal, withoutRetry,
  type CanonicalRefusalPolicy, type MappedCanonicalRefusal, type RefusalStatusReason, type ValidatedRefusalError,
} from './validated-refusal';

/**
 * PS-13 / I-22 · every Panel live port is `definePort(http, ops, policy)`.
 *
 * `IdentityHttpClient` owns authentication and the generated request/response
 * validation; a port only unwraps the admitted envelope and maps a validated
 * refusal. The refusal policy lives here, in `refusalPolicy`, and nowhere else:
 * when the backend refusal envelope (3.1) changes, this is the one function
 * that changes with it.
 */

/** A validated envelope of these kinds is never a refusal: the request was not
 * dispatched, the server failed, or its answer is not trusted. It stays a
 * rejection and never proves whether a mutation committed. */
const NOT_A_REFUSAL: readonly IdentityHttpErrorKind[] = ['network', 'server', 'invalid_request', 'invalid_response'];

/** 408, 429 and 503 (a declared owner 503 only; an undeclared one is a server
 * failure): the owner did not decide, so nothing was written; try again later.
 * One policy for every port: a vocabulary that names it says `transientReason`;
 * the others keep the status reason (`unknown`). It is never a transport
 * failure: a write refused this way must not be held as one whose outcome is
 * unknown. */
const TRANSIENT_STATUSES: readonly number[] = [408, 429, 503];

interface RefusalPolicyOptions<Reason extends string> {
  /** `client_status`: only a validated 4xx envelope is a refusal; a 5xx or a
   * status-less envelope stays a rejection. Default: every validated envelope. */
  readonly admit?: 'validated' | 'client_status';
  /** The reason this port's vocabulary gives 408/429/503. */
  readonly transientReason?: Reason;
  /** The port's own reasons on top of the shared status reason; null keeps it. */
  readonly classify?: (error: ValidatedRefusalError, statusReason: RefusalStatusReason) => Reason | null;
  /** Own reasons a re-read resolves, like `conflict`. */
  readonly refreshReasons?: readonly Reason[];
}

/** What a policy knows about the failed request: whether it could have written. */
export type PortRequest = Readonly<{ mutation: boolean }>;

/** A port's refusal function: a validated refusal becomes the port's value;
 * anything else is rethrown unchanged by `canonicalRefusal` (a vocabulary with
 * its own transport failure maps it here, by `request.mutation`). */
export type PortRefusalPolicy<Refusal> = (error: unknown, request: PortRequest) => Refusal;

/** A refusal function that needs no request context. */
type RefusalMapper<Refusal> = (error: unknown) => Refusal;

/** The one Panel refusal policy (I-22). `retry: false` is for result vocabularies without a retry hint. */
export function refusalPolicy<Reason extends string = never>(
  options: RefusalPolicyOptions<Reason> & { readonly retry: false },
): RefusalMapper<Omit<MappedCanonicalRefusal<Reason>, 'retry'>>;
export function refusalPolicy<Reason extends string = never>(
  options?: RefusalPolicyOptions<Reason> & { readonly retry?: true },
): RefusalMapper<MappedCanonicalRefusal<Reason>>;
export function refusalPolicy<Reason extends string = never>(
  options: RefusalPolicyOptions<Reason> & { readonly retry?: boolean } = {},
): RefusalMapper<MappedCanonicalRefusal<Reason> | Omit<MappedCanonicalRefusal<Reason>, 'retry'>> {
  const { transientReason, classify } = options;
  const policy: CanonicalRefusalPolicy<Reason> = {
    excludedKinds: NOT_A_REFUSAL,
    requireClientStatus: options.admit === 'client_status',
    classify: (error, statusReason) => {
      const transient = error.status !== null && TRANSIENT_STATUSES.includes(error.status);
      return (transient ? transientReason : undefined) ?? classify?.(error, statusReason) ?? null;
    },
    ...(options.refreshReasons ? { refreshReasons: options.refreshReasons } : {}),
  };
  return options.retry === false
    ? (error) => withoutRetry(canonicalRefusal(error, policy))
    : (error) => canonicalRefusal(error, policy);
}

type Unwrapped<Envelope> = Envelope extends Readonly<{ result: infer Result }> ? Result : never;

/** Every port result: the owner's value, or the port's refusal. */
type PortResult<Canonical, Refusal> = Readonly<{ kind: 'success'; canonical: Canonical }> | Refusal;

type ReadHttp = Pick<PanelGeneratedHttpPort, 'read'> & Partial<Pick<PanelGeneratedHttpPort, 'readConditional'>>;
type MutateHttp = Pick<PanelGeneratedHttpPort, 'mutate'>;

interface ReadVerbs<Refusal> {
  /** A generated read whose envelope carries `result`: the port returns `result`. */
  readonly read: <C extends CoreRead>(command: C, options?: PanelReadOptions) => Promise<PortResult<Unwrapped<CoreReadResponseMap[C['operation']]>, Refusal>>;
  /** A generated read whose answer is not a JSON envelope (binary images); the messaging reads until their routes join the envelope. */
  readonly readValue: <C extends CoreRead>(command: C, options?: PanelReadOptions) => Promise<PortResult<CoreReadResponseMap[C['operation']], Refusal>>;
  /** A conditional generated read (`conditional_read: 'etag'`): `not_modified`, or `result` with its revision. */
  readonly readConditional: <C extends CoreRead>(command: C, entityTag: string | null)
    => Promise<PortResult<ConditionalRead<Unwrapped<CoreReadResponseMap[C['operation']]>>, Refusal>>;
  /** A named unwrap the generic verbs cannot express; `mutation` says whether the request can write. */
  readonly settle: <Canonical>(request: () => Promise<Canonical>, options?: PortRequest) => Promise<PortResult<Canonical, Refusal>>;
}

interface MutateVerbs<Refusal> {
  readonly mutate: <C extends CoreExactMutation>(command: C) => Promise<PortResult<Unwrapped<CoreExactMutationResponseMap[C['operation']]>, Refusal>>;
  readonly mutateValue: <C extends CoreExactMutation>(command: C) => Promise<PortResult<CoreExactMutationResponseMap[C['operation']], Refusal>>;
}

export type PortVerbs<Refusal, Http extends ReadHttp> = ReadVerbs<Refusal> & (Http extends MutateHttp ? MutateVerbs<Refusal> : unknown);

/**
 * Binds a screen's operations to the generated transport under one refusal
 * policy. `ops` names each operation's verb; the transport and the policy do
 * the rest.
 */
export function definePort<Http extends ReadHttp & Partial<MutateHttp>, Refusal, Ops>(
  http: Http,
  ops: (verbs: PortVerbs<NoInfer<Refusal>, Http>) => Ops,
  policy: PortRefusalPolicy<Refusal>,
): Ops {
  const settle = <Canonical>(request: () => Promise<Canonical>, options: PortRequest = { mutation: false }): Promise<PortResult<Canonical, Refusal>> =>
    settleRefusal(request, (error) => policy(error, options));
  const write: PortRequest = { mutation: true };
  const mutate = <C extends CoreExactMutation>(command: C) => {
    // Unreachable through the types: a read-only port has no mutate verb.
    if (http.mutate === undefined) throw new TypeError('port_without_mutate');
    return http.mutate(command);
  };
  // A read without an owner signal reaches the transport exactly as before.
  const httpRead = <C extends CoreRead>(command: C, options: PanelReadOptions | undefined) =>
    options === undefined ? http.read(command) : http.read(command, options);
  const verbs: ReadVerbs<Refusal> & MutateVerbs<Refusal> = {
    read: (command, options) => settle(async () => (await httpRead(command, options) as unknown as Readonly<{ result: never }>).result),
    readValue: (command, options) => settle(() => httpRead(command, options)),
    readConditional: (command, entityTag) => settle(async () => {
      // Unreachable through the types for a port built on a client without it.
      if (http.readConditional === undefined) throw new TypeError('port_without_conditional_read');
      const read = await http.readConditional(command, entityTag);
      return read.kind === 'not_modified' ? read
        : Object.freeze({ kind: 'current' as const, value: (read.value as unknown as Readonly<{ result: never }>).result, entityTag: read.entityTag });
    }),
    settle,
    mutate: (command) => settle(async () => (await mutate(command) as unknown as Readonly<{ result: never }>).result, write),
    mutateValue: (command) => settle(() => mutate(command), write),
  };
  return ops(verbs as PortVerbs<Refusal, Http>);
}
